import React, { useState, useEffect } from 'react';
import { restaurantInfoAPI } from '../../services/api';

function RestaurantInfo() {
  const [info, setInfo] = useState({
    restaurant_name: '',
    phone: '',
    email: '',
    address: '',
    address_short: '',
    hours_display: '',
    breakfast_hours: '',
    contact_email_alt: '',
    google_maps_embed: '',
    newsletter_title: '',
    newsletter_text: '',
    booking_text: '',
    visit_us_text: '',
    daily_text: '',
    location_text: '',
    social_media: {
      facebook: '',
      instagram: '',
      twitter: '',
      google_maps: ''
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchRestaurantInfo();
  }, []);

  const fetchRestaurantInfo = async () => {
    try {
      setLoading(true);
      const response = await restaurantInfoAPI.getAll();
      const data = response.data.data;
      
      // Transform API data to component state
      const transformedInfo = { ...info };
      
      Object.keys(data).forEach(key => {
        const item = data[key];
        if (key === 'social_media' && item.data_type === 'json') {
          transformedInfo[key] = typeof item.value === 'string' 
            ? JSON.parse(item.value) 
            : item.value;
        } else {
          transformedInfo[key] = item.value;
        }
      });
      
      setInfo(transformedInfo);
      setError(null);
    } catch (err) {
      console.error('Error fetching restaurant info:', err);
      setError('Failed to load restaurant information');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('social_')) {
      const socialKey = name.replace('social_', '');
      setInfo(prev => ({
        ...prev,
        social_media: {
          ...prev.social_media,
          [socialKey]: value
        }
      }));
    } else {
      setInfo(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccessMessage('');

    try {
      // Prepare fields to update
      const fieldsToUpdate = [
        { key: 'restaurant_name', value: info.restaurant_name },
        { key: 'phone', value: info.phone },
        { key: 'email', value: info.email },
        { key: 'address', value: info.address },
        { key: 'address_short', value: info.address_short },
        { key: 'hours_display', value: info.hours_display },
        { key: 'breakfast_hours', value: info.breakfast_hours },
        { key: 'contact_email_alt', value: info.contact_email_alt },
        { key: 'google_maps_embed', value: info.google_maps_embed },
        { key: 'newsletter_title', value: info.newsletter_title },
        { key: 'newsletter_text', value: info.newsletter_text },
        { key: 'booking_text', value: info.booking_text },
        { key: 'visit_us_text', value: info.visit_us_text },
        { key: 'daily_text', value: info.daily_text },
        { key: 'location_text', value: info.location_text },
        { key: 'social_media', value: JSON.stringify(info.social_media), data_type: 'json' }
      ];

      // Update each field
      const updatePromises = fieldsToUpdate.map(field =>
        restaurantInfoAPI.update(field.key, {
          value: field.value,
          data_type: field.data_type || 'text'
        })
      );

      await Promise.all(updatePromises);
      
      setSuccessMessage('Restaurant information updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (err) {
      console.error('Error saving restaurant info:', err);
      setError('Failed to save restaurant information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p className="mt-3">Loading restaurant information...</p>
      </div>
    );
  }

  return (
    <div className="restaurant-info-management">
      <div className="page-header mb-4">
        <h2 className="page-title">
          <i className="fas fa-info-circle me-3"></i>
          Restaurant Contact Information
        </h2>
        <p className="page-description text-muted">
          Manage all contact information displayed across your website
        </p>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show">
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* Basic Information */}
          <div className="col-lg-8">
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-building me-2"></i>
                  Basic Information
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label htmlFor="restaurant_name" className="form-label">Restaurant Name</label>
                      <input
                        type="text"
                        className="form-control"
                        id="restaurant_name"
                        name="restaurant_name"
                        value={info.restaurant_name}
                        onChange={handleInputChange}
                        placeholder="Restaurant Name"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label htmlFor="phone" className="form-label">Phone Number</label>
                      <input
                        type="text"
                        className="form-control"
                        id="phone"
                        name="phone"
                        value={info.phone}
                        onChange={handleInputChange}
                        placeholder="+962-(6)-541-2484"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label htmlFor="email" className="form-label">Primary Email</label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        value={info.email}
                        onChange={handleInputChange}
                        placeholder="info@cioconatcafe.com"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label htmlFor="contact_email_alt" className="form-label">Alternative Email</label>
                      <input
                        type="email"
                        className="form-control"
                        id="contact_email_alt"
                        name="contact_email_alt"
                        value={info.contact_email_alt}
                        onChange={handleInputChange}
                        placeholder="info@cioconatlounge.com"
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-group mb-3">
                      <label htmlFor="address" className="form-label">Full Address</label>
                      <textarea
                        className="form-control"
                        id="address"
                        name="address"
                        rows="2"
                        value={info.address}
                        onChange={handleInputChange}
                        placeholder="Al Shab St 253, Dabouq, Amman, Jordan"
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-group mb-3">
                      <label htmlFor="address_short" className="form-label">Short Address Format</label>
                      <input
                        type="text"
                        className="form-control"
                        id="address_short"
                        name="address_short"
                        value={info.address_short}
                        onChange={handleInputChange}
                        placeholder="Al-Sha'b St, Dabouq, Amman 11822, JO"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Operating Hours */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-clock me-2"></i>
                  Operating Hours
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label htmlFor="hours_display" className="form-label">Daily Hours (Display Format)</label>
                      <input
                        type="text"
                        className="form-control"
                        id="hours_display"
                        name="hours_display"
                        value={info.hours_display}
                        onChange={handleInputChange}
                        placeholder="9.00 am - 1.00 am"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label htmlFor="breakfast_hours" className="form-label">Breakfast Hours</label>
                      <input
                        type="text"
                        className="form-control"
                        id="breakfast_hours"
                        name="breakfast_hours"
                        value={info.breakfast_hours}
                        onChange={handleInputChange}
                        placeholder="Monday to Sunday 9.00 am - 1.00pm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Labels & Text */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-tags me-2"></i>
                  Custom Labels & Text
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label htmlFor="booking_text" className="form-label">Booking Text</label>
                      <input
                        type="text"
                        className="form-control"
                        id="booking_text"
                        name="booking_text"
                        value={info.booking_text}
                        onChange={handleInputChange}
                        placeholder="Booking request"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label htmlFor="visit_us_text" className="form-label">Visit Us Header</label>
                      <input
                        type="text"
                        className="form-control"
                        id="visit_us_text"
                        name="visit_us_text"
                        value={info.visit_us_text}
                        onChange={handleInputChange}
                        placeholder="Visit Us"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label htmlFor="location_text" className="form-label">Location Label</label>
                      <input
                        type="text"
                        className="form-control"
                        id="location_text"
                        name="location_text"
                        value={info.location_text}
                        onChange={handleInputChange}
                        placeholder="Location"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label htmlFor="daily_text" className="form-label">Daily Hours Label</label>
                      <input
                        type="text"
                        className="form-control"
                        id="daily_text"
                        name="daily_text"
                        value={info.daily_text}
                        onChange={handleInputChange}
                        placeholder="Daily"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="col-lg-4">
            {/* Newsletter */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-envelope me-2"></i>
                  Newsletter Section
                </h5>
              </div>
              <div className="card-body">
                <div className="form-group mb-3">
                  <label htmlFor="newsletter_title" className="form-label">Newsletter Title</label>
                  <input
                    type="text"
                    className="form-control"
                    id="newsletter_title"
                    name="newsletter_title"
                    value={info.newsletter_title}
                    onChange={handleInputChange}
                    placeholder="Get News & Offers"
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="newsletter_text" className="form-label">Newsletter Description</label>
                  <textarea
                    className="form-control"
                    id="newsletter_text"
                    name="newsletter_text"
                    rows="3"
                    value={info.newsletter_text}
                    onChange={handleInputChange}
                    placeholder="enjoy mouthwatering surprises and tempting offers delivered straight to your inbox!"
                  />
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-share-alt me-2"></i>
                  Social Media Links
                </h5>
              </div>
              <div className="card-body">
                <div className="form-group mb-3">
                  <label htmlFor="social_facebook" className="form-label">Facebook URL</label>
                  <input
                    type="url"
                    className="form-control"
                    id="social_facebook"
                    name="social_facebook"
                    value={info.social_media.facebook}
                    onChange={handleInputChange}
                    placeholder="https://www.facebook.com/..."
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="social_instagram" className="form-label">Instagram URL</label>
                  <input
                    type="url"
                    className="form-control"
                    id="social_instagram"
                    name="social_instagram"
                    value={info.social_media.instagram}
                    onChange={handleInputChange}
                    placeholder="https://www.instagram.com/..."
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="social_google_maps" className="form-label">Google Maps URL</label>
                  <input
                    type="url"
                    className="form-control"
                    id="social_google_maps"
                    name="social_google_maps"
                    value={info.social_media.google_maps}
                    onChange={handleInputChange}
                    placeholder="https://goo.gl/maps/..."
                  />
                </div>
              </div>
            </div>

            {/* Google Maps Embed */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-map-marker-alt me-2"></i>
                  Google Maps
                </h5>
              </div>
              <div className="card-body">
                <div className="form-group mb-3">
                  <label htmlFor="google_maps_embed" className="form-label">Google Maps Embed URL</label>
                  <textarea
                    className="form-control"
                    id="google_maps_embed"
                    name="google_maps_embed"
                    rows="3"
                    value={info.google_maps_embed}
                    onChange={handleInputChange}
                    placeholder="https://www.google.com/maps/embed?pb=..."
                  />
                  <small className="form-text text-muted">
                    Get this URL from Google Maps → Share → Embed a map
                  </small>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="card">
              <div className="card-body text-center">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Save Restaurant Information
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      <style jsx>{`
        .restaurant-info-management {
          max-width: 1200px;
        }

        .page-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 2rem;
          border-radius: 15px;
          margin-bottom: 2rem;
        }

        .page-title {
          font-size: 1.8rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .page-description {
          font-size: 1.1rem;
          opacity: 0.9;
          margin-bottom: 0;
        }

        .card {
          border: none;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          border-radius: 15px;
        }

        .card-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          border-radius: 15px 15px 0 0 !important;
          padding: 1.25rem 1.5rem;
        }

        .card-title {
          color: #2c3e50;
          font-weight: 600;
        }

        .form-label {
          font-weight: 600;
          color: #2c3e50;
        }

        .form-control {
          border: 2px solid #e9ecef;
          border-radius: 8px;
          padding: 0.75rem 1rem;
          transition: all 0.3s ease;
        }

        .form-control:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
        }

        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          padding: 0.75rem 2rem;
          border-radius: 50px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
        }

        .btn-primary:disabled {
          opacity: 0.7;
          transform: none;
          box-shadow: none;
        }

        .alert {
          border: none;
          border-radius: 12px;
          padding: 1rem 1.5rem;
        }

        .alert-success {
          background-color: #d4edda;
          color: #155724;
        }

        .alert-danger {
          background-color: #f8d7da;
          color: #721c24;
        }
      `}</style>
    </div>
  );
}

export default RestaurantInfo;